import React, { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays, differenceInCalendarDays, addDays } from "date-fns";
import { toast } from "react-toastify";
import { IoMdDownload } from "react-icons/io";
import { TbReportSearch } from "react-icons/tb";

import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import { exportBookingsExcel } from "../../lib/apis/reports";

const MAX_RANGE_DAYS = 92;

const BookingsExportCard: React.FC = () => {
  const { hospitalID, hospData } = useHospDocData();

  const [fromDate, setFromDate] = useState<Date>(() => subDays(new Date(), 30));
  const [toDate, setToDate] = useState<Date>(() => new Date());
  const [downloading, setDownloading] = useState(false);

  const rangeDays = useMemo(
    () => differenceInCalendarDays(toDate, fromDate) + 1,
    [fromDate, toDate]
  );
  const rangeInvalid = toDate < fromDate;
  const rangeTooLong = rangeDays > MAX_RANGE_DAYS;

  const handleFromChange = (date: Date | null) => {
    if (!date) return;
    setFromDate(date);
    if (date > toDate) setToDate(date);
    const cap = addDays(date, MAX_RANGE_DAYS - 1);
    if (toDate > cap) setToDate(cap);
  };

  const handleToChange = (date: Date | null) => {
    if (!date) return;
    setToDate(date);
  };

  const handleDownload = async () => {
    if (!hospitalID) return;
    if (rangeInvalid) {
      toast.error("'To' date must be on or after 'From' date");
      return;
    }
    if (rangeTooLong) {
      toast.error(`Date range cannot exceed ${MAX_RANGE_DAYS} days`);
      return;
    }

    const from = format(fromDate, "yyyy-MM-dd");
    const to = format(toDate, "yyyy-MM-dd");

    try {
      setDownloading(true);
      const res = await exportBookingsExcel({ hospitalId: hospitalID, from, to });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const hospSlug = (hospData?.name || "hospital")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      a.download = `bookings_${hospSlug}_${from}_to_${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err: any) {
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          toast.error(parsed?.error?.[0]?.msg || parsed?.message || "Export failed");
        } catch {
          toast.error("Export failed");
        }
      } else {
        toast.error(err?.message || "Export failed");
      }
    } finally {
      setDownloading(false);
    }
  };

  const maxToDate = addDays(fromDate, MAX_RANGE_DAYS - 1);
  const today = new Date();

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
      <div className="flex items-center gap-3 mb-2">
        <TbReportSearch className="w-6 h-6 text-[#0F3CBE]" />
        <h1 className="text-base md:text-lg font-semibold">Bookings TAT</h1>
      </div>
      <p className="text-xs md:text-sm text-gray-600 mb-1">
        Download bookings TAT report as an Excel file.
      </p>
      <p className="text-xs md:text-sm text-gray-500 mb-5">
        Pick a date range (up to {MAX_RANGE_DAYS} days) to export all bookings for{" "}
        <span className="font-medium">{hospData?.name || "this hospital"}</span>.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3 max-w-2xl">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            From
          </label>
          <DatePicker
            selected={fromDate}
            onChange={handleFromChange}
            selectsStart
            startDate={fromDate}
            endDate={toDate}
            maxDate={today}
            dateFormat="yyyy-MM-dd"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F3CBE]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            To
          </label>
          <DatePicker
            selected={toDate}
            onChange={handleToChange}
            selectsEnd
            startDate={fromDate}
            endDate={toDate}
            minDate={fromDate}
            maxDate={maxToDate < today ? maxToDate : today}
            dateFormat="yyyy-MM-dd"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F3CBE]"
          />
        </div>
      </div>

      <p
        className={`text-xs mb-5 ${
          rangeTooLong || rangeInvalid ? "text-red-600" : "text-gray-500"
        }`}
      >
        {rangeInvalid
          ? "'To' date must be on or after 'From' date"
          : rangeTooLong
          ? `Range exceeds ${MAX_RANGE_DAYS} days (selected ${rangeDays})`
          : `Selected range: ${rangeDays} day${rangeDays === 1 ? "" : "s"}`}
      </p>

      <button
        onClick={handleDownload}
        disabled={downloading || rangeInvalid || rangeTooLong || !hospitalID}
        className="inline-flex items-center gap-2 bg-[#0F3CBE] hover:bg-[#0d35a8] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors"
      >
        <IoMdDownload className="w-5 h-5" />
        {downloading ? "Preparing..." : "Download Excel"}
      </button>
    </div>
  );
};

export default BookingsExportCard;
